import { normalizeSupportChatMessage } from '@/lib/helpers/support-chat';
import type { SupportChatMessage } from '@/types';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function unwrapPayload(raw: unknown): Record<string, unknown> | null {
  let cur: unknown = raw;

  if (typeof cur === 'string') {
    try {
      cur = JSON.parse(cur) as unknown;
    } catch {
      return null;
    }
  }

  if (Array.isArray(cur) && cur.length > 0) {
    const first = cur[0];
    if (isRecord(first) && typeof first.value === 'string') {
      try {
        cur = JSON.parse(first.value) as unknown;
      } catch {
        return null;
      }
    }
  }

  if (isRecord(cur) && 'data' in cur) {
    const inner = cur.data;
    if (typeof inner === 'string') {
      try {
        cur = JSON.parse(inner) as unknown;
      } catch {
        cur = inner;
      }
    } else {
      cur = inner;
    }
  }

  return isRecord(cur) ? cur : null;
}

function normalizeConversationId(raw: unknown): string | number | null {
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) return raw;
  return null;
}

export type ParsedAiSupportRealtimePayload = {
  conversationId: string | number;
  message: SupportChatMessage;
};

/**
 * Normalizes Socket.IO `ai_support_message` payloads for assistant replies.
 */
export function parseAiSupportNewMessagePayload(
  raw: unknown
): ParsedAiSupportRealtimePayload | null {
  const root = unwrapPayload(raw);
  if (!root) return null;

  const conversationId =
    normalizeConversationId(root.conversationId) ??
    normalizeConversationId(root.conversation_id) ??
    normalizeConversationId(
      isRecord(root.message) ? root.message.conversationId : undefined
    );
  if (conversationId === null) return null;

  const messageRaw = isRecord(root.message) ? root.message : root;
  const normalized = normalizeSupportChatMessage(messageRaw);

  if (!normalized || normalized.sender === 'user') return null;

  return { conversationId, message: normalized };
}
